<template>
  <div class="align-items-center text-center dp02 p-3">
    <div class="text-left">
      <h1 class="mb-4">
        {{ displayNamePlural }}
      </h1>
      <div class="row col-12 justify-content-start">
        <router-link
          v-if="
            allowCreate &&
              $auth.check(
                'Permissions.AdminPanel.' + permissionNameComputed + '.Create'
              )"
          :to="createRouteComputed">
          <b-button class="createNew">
            {{ $t("DataTable.CreateNew") }}
          </b-button>
        </router-link>
        <slot name="extraTableActions" />
      </div>
      <div class="dp04">
        <b-table
          id="dataTable"
          striped
          borderless
          dark
          hover
          responsive
          show-empty
          :no-sort-reset="true"
          :per-page="perPage"
          :current-page="currentPage"
          :busy="!loadingComplete"
          :items="itemsArrFiltered"
          :fields="fields"
          :sort-compare="sortCompare"
          :sort-by="Object.keys(sortBy)[0]"
          :sort-desc="Object.values(sortBy)[0]"
          :tbody-tr-class="rowClass"
          @input="onInput"
          class="my-3">
          <template v-slot:table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle" />
              <strong>{{ $t("DataTable.Loading") }}...</strong>
            </div>
          </template>

          <template
            v-slot:top-row>
            <b-td v-for="f in fields"
                  :key="f.key">
              <div v-if="f.sortable">
                <div v-if="f.key === 'gender'">
                  <GenderFilter v-model="filter[f.key]"
                                :field="f.key"
                                @filter="addFilter(f.key, ...arguments)" />
                </div>
                <div v-else-if="f.key === 'paidBy'">
                  <PaymentStatusFilter v-model="filter[f.key]"
                                       :field="f.key"
                                       @filter="addFilter(f.key, ...arguments)" />
                </div>
                <div v-else-if="f.key === 'status' && (dataModel === 'products' || dataModel === 'products/doctorSection' || dataModel === 'products/archived')">
                  <ProductStatusFilter v-model="filter[f.key]"
                                       :field="f.key"
                                       @filter="addFilter(f.key, ...arguments)" />
                </div>
                <div v-else-if="f.key === 'isUserLoginActive'">
                  <UserActiveFilter v-model="filter[f.key]"
                                    :field="f.key"
                                    @filter="addFilter(f.key, ...arguments)" />
                </div>
                <div v-else-if="riskFields.includes(f.key)">
                  <RiskFilter v-model="filter[f.key]"
                              :field="f.key"
                              @filter="addFilter(f.key, ...arguments)" />
                </div>
                <div v-else-if="boolFields.includes(f.key)">
                  <BoolFilter v-model="filter[f.key]"
                              :field="f.key"
                              @filter="addFilter(f.key, ...arguments)" />
                </div>
                <div v-else-if="dateFields.includes(f.key)">
                  <DateFilter
                    v-model="filter[f.key]"
                    :field="f.key"
                    @filter="addFilter(f.key, ...arguments)" />
                </div>
                <div v-else-if="f.key === 'status' && dataModel === 'customerrelationships'">
                  <CustomerRelationshipFilter v-model="filter[f.key]"
                                              :field="f.key"
                                              @filter="addFilter(f.key, ...arguments)" />
                </div>
                <div v-else>
                  <TextFilter
                    v-model="filter[f.key]"
                    :field="f.key"
                    @filter="addFilter(f.key, ...arguments)" />
                </div>
              </div>
            </b-td>
          </template>
          <template
            v-slot:cell(gender)="row">
            <template v-if="row.item.gender === 1">
              {{ $t('common.Both') }}
            </template>
            <template v-else-if="row.item.gender === 2">
              {{ $t('common.Male') }}
            </template>
            <template v-else-if="row.item.gender === 3">
              {{ $t('common.Female') }}
            </template>
          </template>
          <template v-slot:cell(actions)="row">
            <div style="min-width:130px">
              <el-tooltip
                v-if="allowView"
                :content="$t('common.View')"
                :enterable="false"
                placement="top">
                <b-button class="viewBtn mr-1"
                          @click="view(row.item.id)">
                  <font-awesome-icon class="fa-xs"
                                     icon="search" />
                </b-button>
              </el-tooltip>
              <el-tooltip v-if="
                            allowEdit &&
                              $auth.check(
                                'Permissions.AdminPanel.' + permissionNameComputed + '.Edit'
                              )
                          "
                          :content="$t('common.Edit')"
                          :enterable="false"
                          placement="top">
                <b-button class="editBtn mx-1"
                          @click="edit(row.item.id)">
                  <font-awesome-icon class="fa-xs"
                                     icon="edit" />
                </b-button>
              </el-tooltip>
              <slot name="extraActions"
                    v-bind="row" />
              <el-tooltip v-if="
                            allowDelete &&
                              $auth.check(
                                'Permissions.AdminPanel.' + permissionNameComputed + '.Delete'
                              )
                          "
                          :content="$t('common.Remove')"
                          :enterable="false"
                          placement="top">
                <b-button class="deleteBtn mx-1"
                          @click="toRemove = row.item.id; $refs.removeModal.show()">
                  <font-awesome-icon class="fa-xs"
                                     icon="trash" />
                </b-button>
              </el-tooltip>
            </div>
          </template>
          <template v-for="( slotFn, slotName ) in $scopedSlots"
                    v-slot:[slotName]="slotData">
            <slot :name="slotName"
                  v-bind="slotData" />
          </template>
        </b-table>
      </div>
      <div class="my-3">
        <b-pagination class="px-auto"
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                      aria-controls="dataTable"
                      size="md"
                      align="center" />
      </div>
    </div>
    <b-modal id="modal-center"
             ref="removeModal"
             centered
             :static="true">
      <template #modal-header>
        <div class="d-flex justify-content-center w-100 text-center">
          <h3>
            {{ $t('DataTable.RemoveTitle') }}
          </h3>
        </div>
      </template>
      <p class="my-4 text-center">
        {{ this.removeMessageComputed }}
      </p>
      <ValidationObserver
        v-if="this.removeNote"
        ref="removeObserver"
        v-slot="{invalid}"
        tag="form">
        <div class="row">
          <div class="col-lg-4">
            <label for="removeNoteMessage">{{ $t('fields.note') }}:</label>
          </div>
          <div class="col-lg-6">
            <ValidationProvider
              name="removeNoteMessage"
              rules="required"
              v-slot="{errors, classes}">
              <b-form-input
                v-model="removeNoteMessage"
                :class="{...classes, myInput: true}" />
              <p class="myInputError mt-2">
                {{ errors[0] }}
              </p>
            </ValidationProvider>
          </div>
        </div>
      </ValidationObserver>
      <template #modal-footer="{ cancel, ok }">
        <b-button
          variant="secondary"
          @click="toRemove = 0; $refs.removeModal.hide();">
          {{ $t('common.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          @click="remove();">
          {{ $t('common.Delete') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import TextFilter from '@components/admin/filters/TextFilter';
import DateFilter from '@components/admin/filters/DateFilter';
import GenderFilter from '@components/admin/filters/GenderFilter';
import BoolFilter from '@components/admin/filters/BoolFilter';
import ProductStatusFilter from '@components/admin/filters/ProductStatusFilter';
import RiskFilter from '@components/admin/filters/RiskFilter';
import PaymentStatusFilter from '@components/admin/filters/PaymentStatusFilter';
import UserActiveFilter from '@components/admin/filters/UserActiveFilter';
import CustomerRelationshipFilter from '@components/admin/filters/CustomerRelationshipFilter';

export default {
  components: {
    TextFilter,
    DateFilter,
    GenderFilter,
    ProductStatusFilter,
    BoolFilter,
    RiskFilter,
    PaymentStatusFilter,
    UserActiveFilter,
    CustomerRelationshipFilter
  },
  props: {
    dataModel: {
      type: String,
      required: true
    },
    displayNamePlural: {
      type: String,
      required: true
    },
    permissionName: {
      type: String,
      required: false,
      default() {
        return '';
      }
    },
    excludeFields: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    formatter: {
      type: Object,
      required: false,
      default() {
        return {};
      },
      validator(val) {
        let valid = true;
        Object.keys(val).forEach((x) => {
          if (typeof (val[x]) !== 'function') {
            valid = false;
          }
        });
        return valid;
      }
    },
    displayNameSingular: {
      type: String,
      required: false,
      default: ''
    },
    allowEdit: {
      type: Boolean,
      required: false,
      default: true
    },
    allowDelete: {
      type: Boolean,
      required: false,
      default: true
    },
    allowCreate: {
      type: Boolean,
      required: false,
      default: true
    },
    allowView: {
      type: Boolean,
      required: false,
      default: true
    },
    sortBy: {
      type: Object,
      required: false,
      default() {
        return {
          id: true
        };
      },
      validator(val) {
        return Object.keys(val).length === 1;
      }
    },
    items: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    itemsPerPage: {
      type: Number,
      required: false,
      default: 10
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    removePath: {
      type: String,
      required: false,
      default: ''
    },
    removeMessage: {
      type: String,
      required: false,
      default: ''
    },
    createRoute: {
      type: String,
      required: false,
      default: ''
    },
    removeNote: {
      type: Boolean,
      required: false,
      default: false
    },
    rowClass: {
      type: Function,
      required: false,
      default: () => ''
    }
  },
  computed: {
    displayNameSingularComputed() {
      return this.displayNameSingular === '' ? this.displayNamePlural.substring(0, this.displayNamePlural.length - 1) : this.displayNameSingular;
    },
    itemsArr() {
      if (this.items.length > 0) {
        return this.mapFieldsToHumanReadableForm(this.items);
      }
      if (this.fetchedData.length === 0) {
        //this.loadData();
        return [];
      }

      return this.mapFieldsToHumanReadableForm(this.fetchedData);
    },
    fields() {
      if (this.itemsArr.length > 0) {
        const fields = Object.keys(this.itemsArr[0])
          .filter((x) => !this.excludeFields.includes(x)).map((x) => ({
            key: x,
            sortable: x !== 'color',
            label: this.$t(`fields.${x}`)
          }));
        if (this.allowEdit
        || this.allowDelete
        || this.allowCreate
        || this.allowView
        || Object.keys(this.$scopedSlots).includes('extraActions')) {
          fields.push({ key: 'Actions', label: this.$t('fields.Actions'), sortable: false });
        }
        return fields;
      }
      return [];
    },
    removePathComputed() {
      return this.removePath.length === 0 ? `/admin/${this.dataModel}/delete` : this.removePath;
    },
    removeMessageComputed() {
      return this.removeMessage.length === 0 ? this.$t('DataTable.RemoveMessage', { 1: this.displayNameSingularComputed }) : this.removeMessage;
    },
    permissionNameComputed() {
      return this.permissionName.length === 0 ? this.dataModel : this.permissionName;
    },
    createRouteComputed() {
      return this.createRoute.length === 0 ? `${this.$route.path}create` : { name: this.createRoute };
    },
  },
  watch: {
    loading() {
      this.loadingComplete = !this.loading;
      this.totalRows = this.itemsArr.length;
    },
    itemsArr() {
      this.itemsArrFiltered = this.itemsArr;
    },
    fields() {
      this.fields.forEach((x) => {
        if (x.sortable) {
          if (this.dateFields.includes(x.key)) {
            this.filter[x.key] = { bs: '==', from: '', as: '>', to: '' };
          } else if (this.boolFields.includes(x.key)) {
            this.filter[x.key] = 1;
          } else if (this.riskFields.includes(x.key)) {
            this.filter[x.key] = 0;
          } else if (x.key === 'gender') {
            this.filter[x.key] = 1;
          } else if (x.key === 'status' && this.dataModel === 'products') {
            this.filter[x.key] = 0;
          } else if (x.key === 'paidBy') {
            this.filter[x.key] = -1;
          } else {
            this.filter[x.key] = '';
          }
        }
      });
    }
  },
  data() {
    return {
      loadingComplete: false,
      routes: {},
      currentPage: 1,
      perPage: this.itemsPerPage,
      filter: {},
      filterFuncs: {},
      fetchedData: [],
      totalRows: 0,
      itemsArrFiltered: [],
      dateFields: ['createdAt', 'updatedAt', 'generatedAt', 'publishedAt', 'validTo', 'dateRegistration', 'created', 'receivedAt', 'dateOrderPaid', 'dateOfBirth', 'lastLoginDate'],
      boolFields: ['isEnabled', 'redeemed', 'complete', 'hasBarcode'],
      riskFields: ['risk', 'defaultTemplateRisk'],
      toRemove: 0,
      removeNoteMessage: '',
      showSpinner: true,
      visibleItems: [],
      cancelSource: null
    };
  },
  mounted() {
    this.cancelSource = this.axios.CancelToken.source();
    this.loadData();
  },
  beforeDestroy() {
    if (this.cancelSource) {
      this.cancelSource.cancel('DataTable unmounted');
      this.cancelSource = null;
    }
  },
  methods: {
    onInput(items) {
      this.visibleItems = items;
      this.$emit('input', this.visibleItems);
    },
    sortCompare(aRow, bRow, key, sortDesc, formatter, compareOptions, compareLocale) {
      const a = aRow[key]
      const b = bRow[key]
      if (this.dateFields.includes(key)) {
        var format = "DD.MM.YYYY hh:mm:ss";
        return this.$moment(a, format) - this.$moment(b, format);
      }
      else if (typeof a === 'number' && typeof b === 'number') {
        return a < b ? -1 : a > b ? 1 : 0
      }
      else {
        return this.toString(a, key).localeCompare(this.toString(b, key), compareLocale, compareOptions)
      }
    },
    toString(value, key) {
      if (value === null || typeof value === 'undefined') {
        return ''
      } else if (value instanceof Object) {
        return Object.keys(value)
          .sort()
          .map(key => this.toString(value[key], key))
          .join(' ')
      } else {
        return String(value)
      }
    },
    loadData(showSpinner = true) {
      if (this.items.length === 0) {
        this.loadingComplete = !this.showSpinner || !showSpinner;
        if (!this.loading) {
          if (!this.cancelSource) {
            this.cancelSource = this.axios.CancelToken.source();
          }
          return this.axios
            .get(`/admin/${this.dataModel}`, { cancelToken: this.cancelSource.token })
            .then((res) => {
              this.loadingComplete = true;
              if (res.status === 200 && res.data.result.length > 0) {
                this.fetchedData = res.data.result;
              } else if (res.status === 204) {
                this.fetchedData = [];
              }
              this.totalRows = this.fetchedData.length;
              return this.fetchedData;
            })
            .catch((err) => {
              if (this.axios.isCancel(err)) {
                return undefined;
              }
              return Promise.reject(err);
            })
            .finally(() => this.$emit("loading-finished"));
        }
        this.totalRows = this.items.length;
        return [];
      }
      this.totalRows = this.items.length;
      this.$emit('reloadData');
      return [];
    },
    reload() {
      this.fetchedData = [];
      this.loadData();
    },
    refresh() {
      this.loadData(false);
    },
    removeRowById(id) {
      const idx = this.fetchedData.findIndex((x) => x.id === id);
      if (idx !== -1) {
        this.fetchedData.splice(idx, 1);
        this.totalRows = this.fetchedData.length;
        this.applyFilters();
      }
    },
    addFilter(field, satisfies) {
      this.filterFuncs[field] = satisfies;
      this.applyFilters();
    },
    finishSpinner() {
      this.showSpinner = false;
    },
    applyFilters() {
      let begin = this.itemsArr;
      Object.values(this.filterFuncs).forEach((func) => {
        begin = begin.filter((x) => func()(x));
      });
      this.itemsArrFiltered = begin;
      this.totalRows = this.itemsArrFiltered.length;
    },
    mapFieldsToHumanReadableForm(itemsArr) {
      this.loadingComplete = true;
      if (!itemsArr || itemsArr.length === 0) return itemsArr;

      const keys = Object.keys(itemsArr[0]);
      const dateFormats = {
        createdAt: 'DD.MM.YYYY',
        updatedAt: 'DD.MM.YYYY',
        dateOrderPaid: 'DD.MM.YYYY HH:mm',
        receivedAt: 'DD.MM.YYYY HH:mm',
        created: 'DD.MM.YYYY HH:mm:ss',
        dateRegistration: 'DD.MM.YYYY',
        generatedAt: 'DD.MM.YYYY',
        validTo: 'DD.MM.YYYY',
        uploadedAt: 'DD.MM.YYYY HH:mm:ss',
        dateOfBirth: 'DD.MM.YYYY'
      };
      const activeDateEntries = Object.entries(dateFormats).filter(([k]) => keys.includes(k));
      const hasGender = keys.includes('gender');
      const hasPublishedAt = keys.includes('publishedAt');
      const hasLastLoginDate = keys.includes('lastLoginDate');
      const formatterEntries = Object.entries(this.formatter);

      itemsArr.forEach((x) => {
        if (x.__formatted) return;
        activeDateEntries.forEach(([k, fmt]) => {
          x[k] = this.$moment(x[k]).format(fmt);
        });
        if (hasPublishedAt) {
          x.publishedAt = x.publishedAt === null ? '' : this.$moment(x.publishedAt).format('DD.MM.YYYY');
        }
        if (hasLastLoginDate) {
          x.lastLoginDate = x.lastLoginDate ? this.$moment(x.lastLoginDate).format('DD.MM.YYYY') : '';
        }
        if (hasGender && x.gender != null && typeof x.gender === 'object') {
          x.gender = x.gender.value;
        }
        formatterEntries.forEach(([key, fn]) => {
          x[key] = fn(x[key]);
        });
        Object.defineProperty(x, '__formatted', {
          value: true,
          enumerable: false,
          configurable: false,
          writable: false
        });
      });
      return itemsArr;
    },
    view(entityId) {
      this.$router.push({ name: `admin.${this.dataModel.toLowerCase()}.viewdetail`, params: { id: entityId } });
    },
    edit(entityId) {
      this.$router.push({ name: `admin.${this.dataModel.toLowerCase()}.edit`, params: { id: entityId } });
    },
    async remove() {
      if (this.removeNote) {
        const isValid = await this.$refs.removeObserver.validate();
        if (!isValid) {
          return;
        }
      }

      const request = this.removeNote ? {
        params: {
          Id: this.toRemove,
          Note: this.removeNoteMessage
        }
      } : {
        params: {
          Id: this.toRemove
        }
      };

      const idToRemove = this.toRemove;
      await this.axios.delete(this.removePathComputed, request).then((res) => {
        if (res.status === 204) {
          this.$root.$bvToast.toast(this.$t('DataTable.RemoveSuccess', { 1: this.displayNameSingularComputed }), {
            title: this.$t('DataTable.RemoveSuccessTitle'),
            variant: 'success',
            toaster: 'b-toaster-top-right'
          });
          this.removeNoteMessage = '';
          this.$refs.removeModal.hide();
          this.toRemove = 0;
          this.removeRowById(idToRemove);
        }
      }).catch((res) => {
        this.$refs.removeModal.hide();
        this.toRemove = 0;
        this.removeNoteMessage = '';

        let errorMessage = this.$t('DataTable.RemoveFailure', { 1: this.displayNameSingularComputed });

        if (res && res.data && res.data.errors) {
          errorMessage = `${errorMessage}. (${res.data.errors.join(". ")})`;
        }

        this.$root.$bvToast.toast(errorMessage, {
          title: this.$t('DataTable.RemoveFailureTitle'),
          variant: 'danger',
          toaster: 'b-toaster-top-right'
        });
      });
    }
  }
};
</script>
<style lang="scss" scoped>
@import '@colors';
.createNew {
  color: $text-on-primary;
  background-color: $primary-color;
  background-image: none;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($primary-color, 3%);
    color: $text-on-primary;
  }
}

.viewBtn {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: $secondary-color;
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-color, 10%);
    color: $text-on-secondary;
  }
}

.editBtn {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: $secondary-variant-color;
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}

.deleteBtn {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: $error-surface-color;
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($error-surface-color, 10%);
    color: $text-on-secondary;
  }
}

.btn .svg-inline--fa {
  margin-top: -5px;
}
</style>
